// Global Budget Configuration Storage
// Manages the storage of budget tracking type configuration

import type { BudgetConfiguration, BudgetTrackingType } from '@/types/budget-configuration.types';

const STORAGE_KEY = 'budget-configuration';

/**
 * Get the current budget configuration from localStorage
 */
export function getBudgetConfiguration(): BudgetConfiguration {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading budget configuration:', error);
  }
  
  // Default configuration
  return {
    trackingType: 'none',
  };
}

/**
 * Save budget configuration to localStorage
 */
export function saveBudgetConfiguration(config: BudgetConfiguration): void {
  try {
    const configWithMetadata: BudgetConfiguration = {
      ...config,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configWithMetadata));
  } catch (error) {
    console.error('Error saving budget configuration:', error);
    throw error;
  }
}

/**
 * Get just the tracking type (convenience method)
 */
export function getBudgetTrackingType(): BudgetTrackingType {
  const config = getBudgetConfiguration();
  return config.trackingType;
}

/**
 * Update just the tracking type (convenience method)
 */
export function updateBudgetTrackingType(trackingType: BudgetTrackingType): void {
  const config = getBudgetConfiguration();
  saveBudgetConfiguration({
    ...config,
    trackingType,
  });
}

/**
 * Clear budget configuration (reset to default)
 */
export function clearBudgetConfiguration(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing budget configuration:', error);
  }
}
